import React, { use, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import s from './EventCodeList.module.scss';
import { FormattedMessage, injectIntl } from 'react-intl';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import Banner from '../Banner/Banner';
import { initialEventCodeState } from '../../reducers/initialState';
import Button from 'react-bootstrap/Button';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { Link, useParams } from 'react-router-dom';
import Pagination from '../Pagination/Pagination';
import { DEFAULT_PAGE_SIZE } from '../../constants';
import EventCodeListTable from './EventCodeListTable';
import { WidgetVisibility, IsWidgetAccessible } from '../../utils/widgetManager';
import ModelCategoryConfigurationContentTemplate from '../ModelCategoryTab/ModelCategoryConfigurationContentTemplate';
import SearchBar from '../SearchBar/SearchBar';
import Dropdown from '../WSAControls/DroprdownContainer/Dropdown';
import Breadcrumb from '../WSAControls/Breadcrumb/BreadCrumb'
import { ModelCategoryPermission } from '../../constants';
import { getModelCategoryPermission } from '../../utils';
import Cookies from 'js-cookie';
import moment from 'moment-timezone';
import ConfirmDialog from '../ConfirmDialog/ConfirmDialog';
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import help from '../../assets/help-grey.svg';


const MODELCOMPONENT = 'MODELCOMPONENT';
const FLOWSEQUENCE = 'FLOWSEQUENCE';
const SEARCH_FILTER = 'SEARCH_FILTER';
const SEARCH_FILTER_TYPE_NAME = 'SEARCH_FILTER_TYPE_NAME';
const SEARCH_FILTER_TYPE_CODE = 'SEARCH_FILTER_TYPE_CODE';

const breadcrumbItems = [
  { label: <FormattedMessage id="modelcategory.modelcategory" defaultMessage="Model Category" />, href: '/operational-setup/model-category-list' },
  { label: <FormattedMessage id="eventCodes.eventCodes" defaultMessage="Event Codes" /> } // Last item has no href (current page)
];

const EventCodeList = ({
  getEventCodes,
  setCurrentPage,
  eventCodeManager = { ...initialEventCodeState.eventCode },
  intl,
  userProfileWidgets,
  userModelCategories,
  cleanUp,
  setFilterData,
  publishEventData,
  currentTimezone
}) => {
  const envName = process.env.REACT_APP_ENV_NAME_SHORT;
  const [searchFilter, setSearchFilter] = useState('');
  const [searchfilterType, setSearchFilterType] = useState(SEARCH_FILTER_TYPE_CODE);
  const [filterComponent, setFilterComponent] = useState(0);
  const [filterFlowSequence, setFilterFlowSequence] = useState(0);
  // BCC-26 follow-up: same ConfirmDialog-driven pattern
  // CompanionConfigVersionList.jsx uses for its own delete - publishing
  // pushes straight to every unit under this category's shadow, so it gets
  // the same "are you sure" gate a destructive-ish action does, rather than
  // firing on the bare click.
  const [confirmPublishActive, setConfirmPublishActive] = useState(false);

  let { ModelCategoryId } = useParams();

  useEffect(() => {
    getEventCodes(ModelCategoryId);
  }, [getEventCodes, ModelCategoryId]);

  useEffect(() => {
    return () => {
      cleanUp({  ...initialEventCodeState });
    };
  }, []);

  const onPageChangeHandler = currentPage => {
    setCurrentPage(currentPage);
  };

  // BCC-26 follow-up: opens the confirmation dialog rather than publishing
  // straight away - see confirmPublishActive above.
  const onPublishClick = () => {
    setConfirmPublishActive(true);
  };

  const handlePublishClose = () => {
    setConfirmPublishActive(false);
  };

  // Category-wide, not tied to any single EventCode row - same reasoning
  // CompanionConfigVersion.jsx's onPublishClick has for reusing its own
  // save endpoint with a Publish flag, just triggered from this list screen
  // (already scoped to one ModelCategoryId) instead of a per-row edit form.
  const handlePublishConfirm = () => {
    setConfirmPublishActive(false);
    const updatedBy = Cookies.get(`userid-${envName}`) || 0;
    publishEventData(parseInt(ModelCategoryId, 10), updatedBy);
  };

  const flowSequenceOptions = () => {
    let flowsequenceData = [];
    flowsequenceData.push({
      label: <FormattedMessage id="eventCodes.allFlowSequence" defaultMessage="All Flow Sequences" />,
      value: 0
    });
    
    eventCodeManager.flowSequeceSetpoints.forEach(element => {
      flowsequenceData.push({
        label: element.Name,
        value: element.SetpointId
      });
    });
    return flowsequenceData;
  };

  const modelComponentOptions = () => {
    let modelComponentData = [];

    modelComponentData.push({
      label: <FormattedMessage id="eventCodes.allSensor" defaultMessage="All Sensors" />,
      value: 0
    });

    eventCodeManager.modelComponentPositions.forEach(element => {
      modelComponentData.push({
        label: element.Name,
        value: element.ModelComponentPositionId
      });
    });
    return modelComponentData;
  };

  const searchHandler = value => {
    setSearchFilter(value);
    filteredResults(SEARCH_FILTER, value);
    setCurrentPage(1);
  };

  const onModelComponentChange = e => {
    setFilterComponent(e.value);
    filteredResults(MODELCOMPONENT, e.value);
    setCurrentPage(1);
  };

  const onFlowSequenceChange = e => {
    setFilterFlowSequence(e.value);
    filteredResults(FLOWSEQUENCE, e.value);
    setCurrentPage(1);
  };

  const onTypeChange = e => {
    setSearchFilterType(e.value);
    setCurrentPage(1);
  };

  let typePlaceHolder = intl.formatMessage({ id: 'eventCodes.eventCodes', defaultMessage: 'Event Code' });

  const typeOptions = () => {
    let statusData = [];

    statusData.push({
      label: <FormattedMessage id="eventCodes.eventCode" defaultMessage="Event Code" />,
      value: SEARCH_FILTER_TYPE_CODE
    });

    statusData.push({
      label: <FormattedMessage id="eventCodes.name" defaultMessage="Name" />,
      value: SEARCH_FILTER_TYPE_NAME
    });

    return statusData;
  };

  const filteredResults = (filterType, value) => {
    let filteredList = eventCodeManager.eventCodeList;

    if (filterType === MODELCOMPONENT) {
      if (value !== 0) {
        filteredList = filteredList.filter(function (item) {
          return item.ModelComponentPositionId === value;
        });
      }
    } else {
      if (filterComponent !== 0) {
        filteredList = filteredList.filter(function (item) {
          return item.ModelComponentPositionId === filterComponent;
        });
      }
    }

    if (filterType === FLOWSEQUENCE) {
      if (value !== 0) {
        filteredList = filteredList.filter(function (item) {
          return item.SetpointId === value;
        });
      }
    } else {
      if (filterFlowSequence !== 0) {
        filteredList = filteredList.filter(function (item) {
          return item.SetpointId === filterFlowSequence;
        });
      }
    }

    if (filterType === SEARCH_FILTER && searchfilterType === SEARCH_FILTER_TYPE_CODE) {
      if (value !== '') {
        filteredList = filteredList.filter(function (item) {
          return item.Code.toLowerCase().includes(value.toLowerCase());
        });
      }
    } else {
      if (searchFilter !== '' && searchfilterType === SEARCH_FILTER_TYPE_CODE) {
        filteredList = filteredList.filter(function (item) {
          return item.Code.toLowerCase().includes(searchFilter.toLowerCase());
        });
      }
    }

    if (filterType === SEARCH_FILTER && searchfilterType === SEARCH_FILTER_TYPE_NAME) {
      if (value !== '') {
        filteredList = filteredList.filter(function (item) {
          return item.Name.toLowerCase().includes(value.toLowerCase());
        });
      }
    } else {
      if (searchFilter !== '' && searchfilterType === SEARCH_FILTER_TYPE_NAME) {
        filteredList = filteredList.filter(function (item) {
          return item.Name.toLowerCase().includes(searchFilter.toLowerCase());
        });
      }
    }

    setFilterData({ filteredResults: { filteredResults: filteredList } });
  };

  const eventCodeObj = Object.values(eventCodeManager.filteredResults);

  let messageId = (eventCodeManager && eventCodeManager.displayMessageCode) || 'none';
  const messageText = intl.formatMessage({ id: messageId, defaultMessage: messageId });

  let isModelCategoryOwner = getModelCategoryPermission(userModelCategories, ModelCategoryId, ModelCategoryPermission.Owner)

  const publishDialogTitle = intl.formatMessage({
    id: 'eventCodes.confirmPublish',
    defaultMessage: 'Are you sure you want to publish this event data?'
  });
  const publishDialogBody = intl.formatMessage({
    id: 'eventCodes.confirmPublishBody',
    defaultMessage: 'All units linked to this model category will be affected.'
  });

  return (
    <div className={s.eventCode}>
      {eventCodeManager.isLoading && <LoadingSpinner />}
      <ConfirmDialog
        title={publishDialogTitle}
        body={publishDialogBody}
        onConfirm={handlePublishConfirm}
        onClose={handlePublishClose}
        showDialog={confirmPublishActive}
      />
      <Banner
        failureText={messageText}
        showBanner={eventCodeManager.showBanner}
        status={eventCodeManager.isOpSuccessful}
        successText={messageText}
      />

      <ModelCategoryConfigurationContentTemplate selectedPage="manageEventCodes" userProfileWidgets={userProfileWidgets}>
        <div className={s.contentWrapper} style={WidgetVisibility(userProfileWidgets, 'EVENTCODELIST')}>
          <div className={s.titleRows}>
            <Row>
              <Col sm={6} lg={8}>
                <div className={s.eventCodeHeader}>
                  <div className={s.subHeading}>
                    <FormattedMessage id="eventCodes.eventCodes" defaultMessage="Event Codes" />
                    <span data-unittest="eventCodeDataCount" className={s.circle}>
                      {eventCodeManager.eventCodeList.length}
                    </span>
                  </div>
                </div>
              </Col>
              <Col sm={6} lg={4} className={s.alignRight}>
                {IsWidgetAccessible(userProfileWidgets, 'EVENTCODEPUBLISH') && (
                  <div className={s.publishHeader}>
                    <Button
                      variant="outline-secondary"
                      className={s.publishButton}
                      onClick={onPublishClick}
                      data-unittest="publishEventData"
                    >
                      <FormattedMessage id="eventCodes.publishEventData" defaultMessage="PUBLISH EVENT DATA" />
                    </Button>
                    <img
                      src={help}
                      alt="info"
                      className={s.infoIcon}
                      onClick={e => e.preventDefault()}
                      data-tooltip-id="publishEventDataTooltip"
                      data-tooltip-content={intl.formatMessage({
                        id: 'eventCodes.publishEventDataHint',
                        defaultMessage:
                          'Publishing generates a snapshot of this model category’s current Warning and Alarm event codes, uploads it, and pushes the update to every unit linked to this model category. All linked units will be affected.'
                      })}
                    />
                    <Tooltip id="publishEventDataTooltip" className="publishEventDataTooltip" />
                  </div>
                )}
                {eventCodeManager.EventDataPublishedDateTime && (
                  <p className={s.lastPublished} data-unittest="lastPublishedText">
                    <FormattedMessage id="eventCodes.lastPublished" defaultMessage="Last published" />:{' '}
                    {moment.unix(eventCodeManager.EventDataPublishedDateTime).tz(currentTimezone).format('DD-MMM-YYYY HH:mm:ss z')}
                  </p>
                )}
              </Col>
            </Row>
            <Row>
              <Col sm={12}>
                <div className='pb-3'>
                  <Breadcrumb items={breadcrumbItems} />
                </div>
              </Col>
            </Row>

            <Row>
              <Col sm={6} lg={4}>
                <span className={s.textHeading}>
                  <FormattedMessage id="eventCodes.search" defaultMessage="Search" />
                </span>
              </Col>
            </Row>
            <Row>
              <Col sm={6} lg={6}>
                <SearchBar
                  searchHandler={searchHandler}
                  clearSearchInVisible={false}
                  initialText={searchFilter}
                  placeHolderTextId={
                    searchfilterType === SEARCH_FILTER_TYPE_NAME ? 'eventCodes.searchLabelName' : 'eventCodes.searchLabelCode'
                  }
                  id="txtSearchBar"
                  showFilterType
                  searchfilterType={searchfilterType}
                  onFilterTypeChange={onTypeChange}
                  filterTypeData={typeOptions()}
                  filterPlaceholderText={typePlaceHolder}
                />
              </Col>
            </Row>
            <Col sm={3} lg={7}></Col>
            <Row>
              <Col sm={12}>
                <span className={s.textHeading}>
                  {' '}
                  <FormattedMessage id="eventCodes.filterBy" defaultMessage="Filter By" />
                </span>
              </Col>
            </Row>
            <Row>
              <Col sm={6} lg={3}>
                <Dropdown
                  id="drpComponentId"
                  dataArray={modelComponentOptions()}
                  controlData={{
                    placeholderText: <FormattedMessage id="eventCodes.allSensor" defaultMessage="All Sensors" />,
                    customClassName: ''
                  }}
                  onDropdownChange={e => onModelComponentChange(e)}
                  selectedOption={modelComponentOptions().filter(option => option.value === filterComponent)}
                  disabled={false}
                />
              </Col>
              <Col sm={6} lg={3}>
                <Dropdown
                  id="drpSetpointId"
                  dataArray={flowSequenceOptions()}
                  controlData={{
                    placeholderText: <FormattedMessage id="eventCodes.allFlowSequence" defaultMessage="All Flow Sequences" />,
                    customClassName: ''
                  }}
                  onDropdownChange={e => onFlowSequenceChange(e)}
                  selectedOption={flowSequenceOptions().filter(option => option.value === filterFlowSequence)}
                  disabled={false}
                />
              </Col>
              <Col sm={6} lg={6}>
                {<Link to={`/operational-setup/model-category-configuration/${ModelCategoryId}/event-codes`}>
                  <Button className={s.addbutton} style={WidgetVisibility(userProfileWidgets, `EVENTCODEADDNEW`)}>
                    + <FormattedMessage id="eventCodes.addNew" defaultMessage="+ ADD NEW" />
                  </Button>
                </Link>}
              </Col>
            </Row>
          </div>
          <div className="">
            <Pagination
              data={eventCodeObj}
              pageSize={DEFAULT_PAGE_SIZE}
              onPageChange={onPageChangeHandler}
              startingPage={eventCodeManager.currentPage}
            >
              <EventCodeListTable userProfileWidgets={userProfileWidgets} ModelCategoryId={ModelCategoryId} IsModelCategoryOwner={isModelCategoryOwner} />
            </Pagination>
          </div>
        </div>
      </ModelCategoryConfigurationContentTemplate>
    </div>
  );
};


EventCodeList.propTypes = {
  getEventCodes: PropTypes.func.isRequired,
  setCurrentPage: PropTypes.func.isRequired,
  setPageFilter: PropTypes.func.isRequired,
  currentTimezone: PropTypes.string.isRequired,
  publishEventData: PropTypes.func.isRequired
};

export default injectIntl(EventCodeList);
